import { Injectable, signal } from '@angular/core';
import { CallSession, CallDirection, CallState, CallDisposition } from './call-types';

/**
 * Service for managing call sessions
 * Handles creation, updates, and lifecycle of call sessions
 */
@Injectable({
  providedIn: 'root'
})
export class CallSessionService {
  // Session storage
  private sessions = new Map<string, CallSession>();
  private _callHistory = signal<CallSession[]>([]);

  // Public getter for history
  readonly callHistory = this._callHistory.asReadonly();

  /**
   * Create a new call session
   */
  createSession(params: {
    direction: CallDirection;
    phoneNumber: string;
    leadId?: number;
    state: CallState;
    telnyxCallId?: string;
    customerCallId?: string;
    agentCallId?: string;
    backendCallId?: number;
  }): CallSession {
    const sessionId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const session: CallSession = {
      id: sessionId,
      leadId: params.leadId,
      direction: params.direction,
      phoneNumber: params.phoneNumber,
      startTime: new Date(),
      state: params.state,
      telnyxCallId: params.telnyxCallId,
      customerCallId: params.customerCallId,
      agentCallId: params.agentCallId,
      backendCallId: params.backendCallId
    };

    this.sessions.set(sessionId, session);
    return session;
  }

  /**
   * Get session by ID
   */
  getSession(sessionId: string): CallSession | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * Update session properties
   */
  updateSession(sessionId: string, updates: Partial<CallSession>): void {
    const session = this.sessions.get(sessionId);
    if (session) {
      Object.assign(session, updates);
    }
  }

  /**
   * Finalize session when call ends
   */
  finalizeSession(sessionId: string, endState: CallState): CallSession | null {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return null;
    }

    const endTime = new Date();
    const duration = Math.floor((endTime.getTime() - session.startTime.getTime()) / 1000);

    session.endTime = endTime;
    session.duration = duration;
    session.state = endState;

    // Determine disposition if not set
    if (!session.disposition) {
      session.disposition = this.determineDisposition(endState, duration);
    }

    // Add to history
    this._callHistory.update(history => [{ ...session }, ...history.slice(0, 49)]);

    return session;
  }

  /**
   * Remove session from active sessions
   */
  removeSession(sessionId: string): void {
    this.sessions.delete(sessionId);
  }

  /**
   * Clear all sessions (for cleanup)
   */
  clearAll(): void {
    this.sessions.clear();
  }

  /**
   * Clear stale sessions (older than 5 minutes)
   */
  clearStale(): void {
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);

    for (const [sessionId, session] of this.sessions.entries()) {
      if (session.startTime.getTime() < fiveMinutesAgo) {
        this.sessions.delete(sessionId);
      }
    }
  }

  /**
   * Get all active sessions
   */
  getActiveSessions(): CallSession[] {
    return Array.from(this.sessions.values());
  }

  /**
   * Find session by Telnyx call ID
   */
  findByTelnyxId(telnyxCallId: string): CallSession | undefined {
    for (const session of this.sessions.values()) {
      if (session.telnyxCallId === telnyxCallId ||
          session.customerCallId === telnyxCallId ||
          session.agentCallId === telnyxCallId) {
        return session;
      }
    }
    return undefined;
  }

  /**
   * Find session by backend call ID
   */
  findByBackendId(backendCallId: number): CallSession | undefined {
    for (const session of this.sessions.values()) {
      if (session.backendCallId === backendCallId) {
        return session;
      }
    }
    return undefined;
  }

  /**
   * Determine final disposition based on call state and duration
   */
  private determineDisposition(endState: CallState, duration: number): CallDisposition {
    switch (endState) {
      case 'ended':
        return duration > 0 ? 'answered' : 'no-answer';
      case 'failed':
        return 'failed';
      default:
        return 'no-answer';
    }
  }
}
